import {
    DEFAULT_EDGE_WRAP_ORDER,
    LAYOUT_VERSION,
    PANEL_KIND_CENTER,
    PANEL_KIND_TOOL,
    normalizeDockZone,
    normalizePanelKind,
} from './constants.js';
import {
    applyPanelKindsToTabs,
    ensureLayoutStateRegions,
    inferRegionsFromTree,
} from './layoutRegions.js';
import { buildLayoutFromBlueprint } from './layoutBlueprint.js';
import {
    buildDefaultTree,
    buildLayoutFromPanels,
    cloneTree,
    inferEdgeWrapOrderFromTree,
    validateLayoutNode,
} from './layoutTree.js';

function normalizeEdgeWrapOrder(order, fallback = DEFAULT_EDGE_WRAP_ORDER) {
    if (!Array.isArray(order)) return [...fallback];
    const valid = order
        .map((e) => normalizeDockZone(e))
        .filter((e) => e !== 'center');
    return valid.length ? valid : [...fallback];
}

export function createEmptyState() {
    const edgeWrapOrder = [...DEFAULT_EDGE_WRAP_ORDER];
    return {
        version: LAYOUT_VERSION,
        root: null,
        regions: {
            left: null,
            right: null,
            top: null,
            bottom: null,
            center: null,
        },
        panels: {},
        hiddenPanelIds: [],
        edgeWrapOrder,
        rootEdgeOrder: [...edgeWrapOrder],
    };
}

/**
 * @param {object} state
 * @param {{ ephemeralPanelIds?: Set<string> }} [options]
 */
export function exportLayout(state, options = {}) {
    const ephemeral = options.ephemeralPanelIds || new Set();
    let root = state.root ? cloneTree(state.root) : null;
    if (root && ephemeral.size) {
        root = filterEphemeralPanelsFromTree(root, ephemeral);
    }
    const edgeWrapOrder = normalizeEdgeWrapOrder(
        state.rootEdgeOrder || state.edgeWrapOrder,
    );
    return {
        version: LAYOUT_VERSION,
        root,
        regions: { ...(state.regions || {}) },
        panels: { ...(state.panels || {}) },
        hiddenPanelIds: [...(state.hiddenPanelIds || [])],
        edgeWrapOrder,
        rootEdgeOrder: [...edgeWrapOrder],
    };
}

function filterEphemeralPanelsFromTree(node, ephemeral) {
    if (!node) return null;
    if (node.type === 'panel') {
        return ephemeral.has(node.panelId) ? null : node;
    }
    if (node.type === 'tabs') {
        const panelIds = (node.panelIds || []).filter((id) => !ephemeral.has(id));
        if (!panelIds.length) return null;
        if (panelIds.length === 1) {
            return { type: 'panel', id: node.id, panelId: panelIds[0] };
        }
        return {
            ...node,
            panelIds,
            activePanelId: panelIds.includes(node.activePanelId)
                ? node.activePanelId
                : panelIds[0],
        };
    }
    if (node.type === 'centerSlot') {
        const child = filterEphemeralPanelsFromTree(node.child, ephemeral);
        return { ...node, child };
    }
    if (node.type === 'split') {
        const children = (node.children || [])
            .map((ch) => filterEphemeralPanelsFromTree(ch, ephemeral))
            .filter(Boolean);
        if (!children.length) return null;
        if (children.length === 1) return children[0];
        const sizes = (node.sizes || []).slice(0, children.length);
        while (sizes.length < children.length) sizes.push(1 / children.length);
        return { ...node, children, sizes };
    }
    return null;
}

function migrateV1ToV2(json, registered, panelZones, panelKinds) {
    let root = json.root ? cloneTree(json.root) : null;
    const panels = {};

    for (const id of registered) {
        panels[id] = {
            ...(json.panels?.[id] || {}),
            pinned: json.panels?.[id]?.pinned !== false,
            panelKind: normalizePanelKind(
                panelKinds[id],
                panelZones[id],
            ),
        };
    }

    if (root) {
        root = applyPanelKindsToTabs(root, panels);
    }

    const zoneMap = {};
    for (const id of registered) {
        zoneMap[id] =
            panelZones[id] ||
            (panels[id]?.panelKind === 'center' ? 'center' : null);
    }

    const regions = inferRegionsFromTree(root, zoneMap);

    const edgeWrapOrder = root
        ? inferEdgeWrapOrderFromTree(root, zoneMap)
        : [...DEFAULT_EDGE_WRAP_ORDER];

    return {
        version: LAYOUT_VERSION,
        root,
        regions,
        panels,
        hiddenPanelIds: [...(json.hiddenPanelIds || [])],
        edgeWrapOrder,
        rootEdgeOrder: [...edgeWrapOrder],
    };
}

/**
 * @param {object} json
 * @param {{ registeredPanelIds?: string[], defaultOrientation?: string, panelZones?: Record<string, string>, panelKinds?: Record<string, string>, regionBlueprints?: Record<string, object> }} options
 */
export function importLayout(json, options = {}) {
    const registered = new Set(options.registeredPanelIds || []);
    const orientation = options.defaultOrientation || 'horizontal';
    const panelZones = options.panelZones || {};
    const panelKinds = options.panelKinds || {};

    if (!json || typeof json !== 'object') {
        return buildFreshState(
            [...registered],
            orientation,
            panelZones,
            panelKinds,
            options.regionBlueprints,
        );
    }

    if (json.version === 1 || !json.regions) {
        return migrateV1ToV2(json, registered, panelZones, panelKinds);
    }

    if (json.version !== LAYOUT_VERSION) {
        console.warn(
            'MultipanelDockLayout: unsupported layout version',
            json.version,
        );
    }

    const hiddenPanelIds = Array.isArray(json.hiddenPanelIds)
        ? json.hiddenPanelIds.filter((id) => registered.has(id))
        : [];

    const panels = {};
    for (const id of registered) {
        panels[id] = {
            pinned: true,
            panelKind: normalizePanelKind(panelKinds[id], panelZones[id]),
            ...(json.panels?.[id] || {}),
        };
    }

    let root = json.root ? cloneTree(json.root) : null;

    if (root && registered.size > 0) {
        const valid = validateLayoutNode(root, registered);
        if (!valid) {
            console.warn(
                'MultipanelDockLayout: layout root failed validation, using default',
            );
            root = null;
        } else {
            root = filterTreeToRegistered(root, registered);
            root = applyPanelKindsToTabs(root, panels);
        }
    }

    const visibleIds = [...registered].filter((id) => !hiddenPanelIds.includes(id));

    let regions = json.regions || inferRegionsFromTree(root, panelZones);

    if (!root && visibleIds.length > 0) {
        const built = buildInitialLayout(
            visibleIds,
            orientation,
            panelZones,
            panelKinds,
            options.regionBlueprints,
        );
        root = built.root;
        regions = built.regions;
    } else {
        regions = ensureLayoutStateRegions({ root, regions }, panelZones);
    }

    const edgeWrapOrder = json.rootEdgeOrder || json.edgeWrapOrder
        ? normalizeEdgeWrapOrder(json.rootEdgeOrder || json.edgeWrapOrder)
        : root
          ? inferEdgeWrapOrderFromTree(root, panelZones)
          : [...DEFAULT_EDGE_WRAP_ORDER];

    return {
        version: LAYOUT_VERSION,
        root,
        regions,
        panels,
        hiddenPanelIds,
        edgeWrapOrder,
        rootEdgeOrder: [...edgeWrapOrder],
    };
}

function filterTreeToRegistered(node, registered) {
    if (!node) return null;

    if (node.type === 'panel') {
        return registered.has(node.panelId) ? node : null;
    }

    if (node.type === 'tabs') {
        const panelIds = (node.panelIds || []).filter((id) => registered.has(id));
        if (panelIds.length === 0) return null;
        if (panelIds.length === 1) {
            return { type: 'panel', id: node.id, panelId: panelIds[0] };
        }
        return {
            ...node,
            panelIds,
            activePanelId: panelIds.includes(node.activePanelId)
                ? node.activePanelId
                : panelIds[0],
        };
    }

    if (node.type === 'centerSlot') {
        const child = filterTreeToRegistered(node.child, registered);
        return { ...node, child };
    }

    if (node.type === 'split') {
        const children = (node.children || [])
            .map((ch) => filterTreeToRegistered(ch, registered))
            .filter(Boolean);
        if (children.length === 0) return null;
        if (children.length === 1) return children[0];
        const sizes = (node.sizes || []).slice(0, children.length);
        while (sizes.length < children.length) sizes.push(1 / children.length);
        return { ...node, children, sizes };
    }

    return null;
}

function buildInitialLayout(
    visibleIds,
    orientation,
    panelZones,
    panelKinds,
    regionBlueprints = null,
) {
    const panelDefs = visibleIds.map((id) => ({
        id,
        zone: panelZones[id],
        panelKind: normalizePanelKind(panelKinds[id], panelZones[id]),
    }));

    if (regionBlueprints && Object.keys(regionBlueprints).length > 0) {
        const merged = mergeUnclaimedPanelsIntoBlueprints(
            regionBlueprints,
            panelDefs,
            panelKinds,
        );
        const { root, regions } = buildLayoutFromBlueprint(
            merged,
            panelZones,
            panelKinds,
        );
        const panels = {};
        for (const id of visibleIds) {
            panels[id] = {
                pinned: true,
                panelKind: normalizePanelKind(panelKinds[id], panelZones[id]),
            };
        }
        return {
            root: root ? applyPanelKindsToTabs(root, panels) : null,
            regions,
            panels,
            edgeWrapOrder: [...DEFAULT_EDGE_WRAP_ORDER],
        };
    }

    const hasZones = visibleIds.some((id) => panelZones[id]);
    if (hasZones) {
        const { root, regions } = buildLayoutFromPanels(panelDefs);
        const panels = {};
        for (const id of visibleIds) {
            panels[id] = {
                pinned: true,
                panelKind: normalizePanelKind(panelKinds[id], panelZones[id]),
            };
        }
        return {
            root: root ? applyPanelKindsToTabs(root, panels) : null,
            regions,
            panels,
            edgeWrapOrder: [...DEFAULT_EDGE_WRAP_ORDER],
        };
    }

    const root = buildDefaultTree(visibleIds, orientation);
    return {
        root,
        regions: inferRegionsFromTree(root, panelZones),
        edgeWrapOrder: [...DEFAULT_EDGE_WRAP_ORDER],
        panels: Object.fromEntries(
            visibleIds.map((id) => [
                id,
                {
                    pinned: true,
                    panelKind: normalizePanelKind(panelKinds[id], panelZones[id]),
                },
            ]),
        ),
    };
}

function mergeUnclaimedPanelsIntoBlueprints(
    regionBlueprints,
    panelDefs,
    panelKinds = {},
) {
    const claimed = new Set();
    const collectIds = (node) => {
        if (!node) return;
        if (node.type === 'panel' && node.panelId) claimed.add(node.panelId);
        if (node.type === 'tabGroup') {
            for (const id of node.panelIds || []) claimed.add(id);
        }
        if (node.type === 'split') {
            for (const ch of node.children || []) collectIds(ch);
        }
    };
    for (const node of Object.values(regionBlueprints)) collectIds(node);

    const merged = { ...regionBlueprints };

    for (const p of panelDefs) {
        if (claimed.has(p.id)) continue;
        const kind = normalizePanelKind(panelKinds[p.id], p.zone);
        if (kind === PANEL_KIND_CENTER) {
            merged.center = { type: 'panel', panelId: p.id };
            claimed.add(p.id);
        }
    }

    const byZone = {};
    for (const p of panelDefs) {
        if (claimed.has(p.id)) continue;
        const z = p.zone || 'center';
        if (!byZone[z]) byZone[z] = [];
        byZone[z].push(p.id);
    }
    for (const [z, ids] of Object.entries(byZone)) {
        if (merged[z]) continue;
        if (ids.length === 1) {
            merged[z] = { type: 'panel', panelId: ids[0] };
        } else {
            merged[z] = {
                type: 'tabGroup',
                panelIds: ids,
                tabPlacement: 'bottom',
            };
        }
    }
    return merged;
}

function buildFreshState(
    panelIds,
    orientation,
    panelZones = {},
    panelKinds = {},
    regionBlueprints = null,
) {
    const visible = panelIds.filter(Boolean);
    const built = buildInitialLayout(
        visible,
        orientation,
        panelZones,
        panelKinds,
        regionBlueprints,
    );
    return {
        version: LAYOUT_VERSION,
        root: built.root,
        regions: built.regions,
        panels: built.panels,
        hiddenPanelIds: [],
        edgeWrapOrder:
            built.edgeWrapOrder || [...DEFAULT_EDGE_WRAP_ORDER],
    };
}
